import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { Container, Section, Heading, Lede, Eyebrow, LinkButton } from "../../components/ui";
import { Seo } from "../../lib/head";
import { CtaBand, faqPageJsonLd, FaqSection, type Faq } from "../../components/marketing";

interface Tag {
  code: string;
  name: string;
  cfr: string;
  look: string;
  items: string[];
}

const TAGS: Tag[] = [
  {
    code: "F756",
    name: "Drug Regimen Review",
    cfr: "§483.45(c)",
    look: "A licensed pharmacist reviewed each resident's regimen at least monthly; irregularities were reported in writing to the attending physician and the DON / Medical Director; each report was acted on.",
    items: [
      "A dated monthly DRR by a licensed pharmacist for every resident, including new admits and short-stays",
      "Each irregularity report delivered to both the attending physician and the DON",
      "The prescriber's documented response (accept / reject with rationale) to every flagged irregularity",
      "Loop closure — unresolved irregularities escalated to the Medical Director",
      "A date-stamped audit trail connecting flag → report → response for a given month",
    ],
  },
  {
    code: "F757",
    name: "Unnecessary Drugs",
    cfr: "§483.45(d)",
    look: "Each drug is free of the five triggers — excessive dose (incl. duplicate therapy), excessive duration, inadequate monitoring, inadequate indication, or adverse consequences.",
    items: [
      "A documented clinical indication for use for every active medication",
      "Stop / review dates for time-limited drugs (antibiotics, PPIs, opioids, hypnotics)",
      "Monitoring parameters / labs for high-risk drugs (anticoagulants, antiepileptics, diuretics)",
      "A duplicate-therapy / drug-drug interaction screen consistent with Beers / STOPP",
      "Taper or discontinuation rationale where duration exceeds evidence-based norms",
    ],
  },
  {
    code: "F605",
    name: "Psychotropic Drugs & Freedom from Chemical Restraints",
    cfr: "§483.12(a)(2); §483.45(e) — absorbs the retired F758",
    look: "No psychoactive drug used for discipline or staff convenience that isn't required to treat the resident's assessed medical symptoms; psychotropics tied to a documented diagnosis; PRN time limits observed; GDR attempted or clinically justified as contraindicated.",
    items: [
      "The medical symptom each psychoactive drug is treating (not a behavior that inconveniences staff)",
      "The drug is at the lowest effective dose with a monitored response",
      "Non-pharmacologic approaches attempted before / alongside the drug",
      "The consultant pharmacist flag where a regimen appeared to function as a chemical restraint, plus the provider response",
      "A PRN psychotropic order ≤14 days, extended only with documented rationale and a defined duration",
      "PRN antipsychotic orders ≤14 days not renewed without a prescriber in-person evaluation",
      "A GDR attempt in two separate quarters in the first year (then annually), or a contraindicated rationale",
      "The documented target symptom, diagnosis, and non-pharmacologic interventions tried",
      "Informed consent / risk discussion for antipsychotic use where policy requires it",
    ],
  },
  {
    code: "F881",
    name: "Antibiotic Stewardship Program",
    cfr: "§483.80(a)(3)",
    look: "An antibiotic stewardship program within the Infection Prevention & Control Program — use protocols, tracking, and a review / feedback loop.",
    items: [
      "Written antibiotic-use protocols (e.g., McGeer / Loeb minimum criteria) in the decision trail",
      "Indication, dose, duration, and a stop / review date for each antibiotic start",
      "Culture / sensitivity-driven de-escalation, or a documented rationale for empiric continuation",
      "The pharmacist's stewardship review and feedback to prescribers",
    ],
  },
];

const FAQS: Faq[] = [
  {
    q: "Is this checklist official CMS guidance?",
    a: "No. It's survey-preparation guidance aligned to the CMS State Operations Manual, Appendix PP, and ASCP/Beers/STOPP criteria. Verify against your state's current interpretive guidance; it doesn't guarantee compliance or the absence of citations, and implies no CMS or ASCP endorsement.",
  },
  {
    q: "How does WeConsult Rx automate this?",
    a: "The rules engine flags F756/F757/F605/F881 triggers as you review, and the e-signed report bundle — the monthly irregularity report, prescriber/DON signatures, and provider-response loop — becomes one dated, survey-ready record.",
  },
];

export function FtagChecklist() {
  const total = useMemo(() => TAGS.reduce((n, t) => n + t.items.length, 0), []);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const done = Object.values(checked).filter(Boolean).length;

  return (
    <>
      <Seo
        title="SNF F-Tag Readiness Checklist for Consultant Pharmacists"
        description="A free F-tag survey-readiness checklist mapping the monthly medication regimen review to CMS F756, F757, F605, and F881. Built for consultant pharmacists — no PHI."
        path="/resources/f-tag-readiness-checklist"
        jsonLd={faqPageJsonLd(FAQS)}
      />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Resource · Survey prep</Eyebrow>
            <Heading level={1} className="mt-3">
              The SNF F-Tag Readiness Checklist
            </Heading>
            <Lede className="mt-6">
              A field checklist that maps your monthly medication regimen review to the CMS F-tags surveyors
              actually cite — so your documentation is defensible before anyone walks in the door.
            </Lede>
            <p className="mt-4 text-sm text-ink-500">
              Aligned to the CMS State Operations Manual, Appendix PP · ASCP / Beers / STOPP guidance · use
              synthetic data only, never PHI.
            </p>
          </div>
        </Container>
      </Section>

      <Section tone="soft" className="pt-0">
        <Container>
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-border bg-surface px-5 py-3">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-forest-soft">
              <div
                className="h-full rounded-full bg-forest-700 transition-[width] duration-300 motion-reduce:transition-none"
                style={{ width: `${total ? (done / total) * 100 : 0}%` }}
              />
            </div>
            <span className="font-mono text-sm tabular-nums text-ink-600">
              {done} / {total}
            </span>
          </div>

          <div className="space-y-6">
            {TAGS.map((t) => (
              <div key={t.code} className="overflow-hidden rounded-2xl border border-border bg-surface">
                <div className="flex flex-wrap items-baseline gap-3 px-6 pt-5">
                  <span className="rounded-lg bg-forest-700 px-2.5 py-1 font-mono text-sm font-bold text-white">
                    {t.code}
                  </span>
                  <span className="text-lg font-bold text-ink-900">{t.name}</span>
                  <span className="font-mono text-xs text-ink-400">{t.cfr}</span>
                </div>
                <p className="px-6 pt-2 text-sm text-ink-600">
                  <span className="font-mono text-[0.65rem] uppercase tracking-wide text-forest-700">What surveyors look for</span>{" "}
                  {t.look}
                </p>
                <ul className="mt-3">
                  {t.items.map((item, i) => {
                    const id = `${t.code}-${i}`;
                    const on = !!checked[id];
                    return (
                      <li key={id} className="border-t border-border">
                        <label className="flex cursor-pointer items-start gap-3 px-6 py-3 hover:bg-forest-soft/50">
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => setChecked((c) => ({ ...c, [id]: !c[id] }))}
                            className="sr-only"
                          />
                          <span
                            className={`mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-md border-2 transition-colors ${
                              on ? "border-forest-700 bg-forest-700 text-white" : "border-border bg-surface"
                            }`}
                            aria-hidden
                          >
                            {on ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                          </span>
                          <span className={`text-[0.9375rem] leading-relaxed ${on ? "text-ink-400 line-through" : "text-ink-800"}`}>
                            {item}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <LinkButton to="/survey-readiness">How WeConsult Rx automates this</LinkButton>
            <LinkButton to="/resources/gdr-documentation-template" variant="secondary">
              GDR documentation template
            </LinkButton>
          </div>
        </Container>
      </Section>

      <FaqSection items={FAQS} />

      <CtaBand
        title="Make the documentation build itself"
        body="See the platform flag F-tag triggers as you review and capture a dated, e-signed record for your next survey."
        primary={{ to: "/demo", label: "Book a demo" }}
        secondary={{ to: "/survey-readiness", label: "Survey readiness" }}
      />
    </>
  );
}
